import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Edit, Search, ChevronLeft, ChevronRight, Store, Plus } from 'lucide-react';
import { fetchRetailers, fetchRetailersCount, fetchCountriesForDropdown } from '../services/api.ts';
import './Retailers.css';

interface Retailer {
  id: number;
  name: string;
  short_name: string;
  logo: string;
  enable: boolean;
  country_id: number;
  country_name: string | null;
}

interface Country {
  id: number;
  name: string;
  code: string;
}

const Retailers: React.FC = () => {
  const navigate = useNavigate();
  const [retailers, setRetailers] = useState<Retailer[]>([]);
  const [countries, setCountries] = useState<Country[]>([]);
  const [totalRetailers, setTotalRetailers] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'inactive'>('all');
  const [filterCountry, setFilterCountry] = useState<number | undefined>(undefined);
  const [currentPage, setCurrentPage] = useState(1);
  const retailersPerPage = 12;

  useEffect(() => {
    const loadCountries = async () => {
      try {
        const data = await fetchCountriesForDropdown();
        setCountries(data);
      } catch (err) {
        console.error('Error loading countries:', err);
      }
    };
    loadCountries();
  }, []);

  useEffect(() => {
    const loadRetailers = async () => {
      try {
        setLoading(true);
        const offset = (currentPage - 1) * retailersPerPage;
        const [retailersData, countData] = await Promise.all([
          fetchRetailers(offset, retailersPerPage, filterCountry),
          fetchRetailersCount(filterCountry)
        ]);
        setRetailers(retailersData || []);
        setTotalRetailers(countData || 0);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load retailers');
        console.error('Error loading retailers:', err);
      } finally {
        setLoading(false);
      }
    };

    loadRetailers();
  }, [currentPage, filterCountry]);

  const filteredRetailers = (retailers || []).filter((retailer) => {
    const matchesSearch = 
      (retailer.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (retailer.short_name || '').toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus =
      filterStatus === 'all' ||
      (filterStatus === 'active' && retailer.enable) ||
      (filterStatus === 'inactive' && !retailer.enable);

    // Country filtering is now done server-side
    return matchesSearch && matchesStatus;
  });

  const totalPages = Math.ceil(totalRetailers / retailersPerPage);

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  if (loading) {
    return (
      <div className="retailers-page">
        <div className="loading">
          <div className="loading-spinner"></div>
          <p>Loading retailers...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="retailers-page">
        <div className="error">Error: {error}</div>
      </div>
    );
  }

  return (
    <div className="retailers-page">
      <div className="page-header">
        <h2>Retailers</h2>
        <div className="page-actions">
          <div className="search-box">
            <Search size={18} />
            <input
              type="text"
              placeholder="Search retailers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <button className="add-btn" onClick={() => navigate('/dashboard/retailers/new')}>
            <Plus size={18} />
            Add New Retailer
          </button>
        </div>
      </div>

      <div className="filter-section">
        <div className="filter-group">
          <label>Country:</label>
          <select 
            value={filterCountry ?? ''} 
            onChange={(e) => {
              const value = e.target.value;
              setFilterCountry(value === '' ? undefined : parseInt(value, 10));
              setCurrentPage(1); // Reset to first page when filter changes
            }}
          >
            <option value="">All Countries</option>
            {countries.map((country) => (
              <option key={country.id} value={country.id}>
                {country.name}
              </option>
            ))}
          </select>
        </div>
        <div className="filter-group">
          <label>Status:</label>
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value as any)}>
            <option value="all">All</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
        <div className="filter-group">
          <label>Showing:</label>
          <span style={{ color: '#6366f1', fontWeight: 600 }}>
            {filteredRetailers.length} of {totalRetailers} retailers
          </span>
        </div>
      </div>

      {filteredRetailers.length === 0 ? (
        <div className="empty-state">
          <p>No retailers found matching your criteria.</p>
        </div>
      ) : (
        <>
          <div className="retailers-table-container">
            <table className="retailers-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Logo</th>
                  <th>Name</th>
                  <th>Short Name</th>
                  <th>Country</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredRetailers.map((retailer) => (
                  <tr key={retailer.id}>
                    <td className="id-cell">{retailer.id}</td>
                    <td className="logo-cell">
                      {retailer.logo ? (
                        <img 
                          src={retailer.logo} 
                          alt={retailer.name}
                          className="retailer-logo"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                            (e.currentTarget.nextElementSibling as HTMLElement)?.classList.remove('hidden');
                          }}
                        />
                      ) : null}
                      <div className={`retailer-placeholder ${retailer.logo ? 'hidden' : ''}`}>
                        <Store size={20} />
                      </div>
                    </td>
                    <td className="name-cell">
                      <span className="retailer-name">{retailer.name}</span>
                    </td>
                    <td className="shortname-cell">
                      {retailer.short_name}
                    </td>
                    <td className="country-cell">
                      {retailer.country_name || 'N/A'}
                    </td>
                    <td className="status-cell">
                      <span className={`status-badge ${retailer.enable ? 'active' : 'inactive'}`}>
                        {retailer.enable ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="actions-cell">
                      <button
                        className="action-btn edit"
                        onClick={() => navigate(`/dashboard/retailers/edit/${retailer.id}`)}
                        title="Edit"
                      >
                        <Edit size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="pagination">
              <button
                className="pagination-btn"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                <ChevronLeft size={18} />
                Previous
              </button>

              <div className="pagination-info">
                Page {currentPage} of {totalPages}
              </div>

              <button
                className="pagination-btn"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                Next
                <ChevronRight size={18} />
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Retailers;
